import { ChatMessage, ManifestationData, ManifestationStep, SavedAffirmation } from "../types";
import { stepPrompts, getNextStep, formatAffirmationOptions, getRandomMessage, rephraseToPresentTense } from "./manifestationSteps";
import { saveAffirmations, loadAffirmations } from "./storage";

// Deterministic rotation of visualization angles so cycles don't repeat
export const VISUALIZATION_ANGLES: string[] = [
  "What does a regular morning look like for you in this reality?",
  "Who are the people closest to you in this version of your life, and how do they see you?",
  "What's changed about how you move through the world day to day?",
  "What are you doing now that you simply couldn't do before?",
  "What's the very first thing you notice when you wake up in this life?",
  "What conversations are you having now that you weren't having before?",
  "What's one small but meaningful detail in your new daily routine?",
  "How do the people closest to you react to this version of you?"
];



interface ProcessOptions {
  isAffirmationSelection?: boolean;
  selectedAffirmationIndex?: number | null;
}

// Function to mark an affirmation as completed
const markAffirmationAsCompleted = async (affirmation: string, affirmations: SavedAffirmation[]): Promise<SavedAffirmation[]> => {
  console.log("Marking affirmation as completed:", affirmation);
  
  // Create a deep copy to avoid mutation issues
  const updatedAffirmations = JSON.parse(JSON.stringify(affirmations)) as SavedAffirmation[];
  
  // Find the affirmation in the saved list
  const affirmationIndex = updatedAffirmations.findIndex(
    aff => aff.text.toLowerCase() === affirmation.toLowerCase()
  );
  
  if (affirmationIndex >= 0) {
    // Mark the existing affirmation as completed
    updatedAffirmations[affirmationIndex].completed = true;
    updatedAffirmations[affirmationIndex].completedAt = new Date();
    console.log("Found and marked existing affirmation as completed");
  } else {
    // If not found, create a new completed affirmation
    const newAffirmation: SavedAffirmation = {
      id: generateUniqueId(),
      text: affirmation,
      createdAt: new Date(),
      completed: true,
      completedAt: new Date()
    };
    updatedAffirmations.push(newAffirmation);
    console.log("Created new completed affirmation");
  }
  
  // Save the updated affirmations to storage
  try {
    await saveAffirmations(updatedAffirmations);
    console.log("Successfully saved completed affirmation");
  } catch (error) {
    console.error("Error saving completed affirmation:", error);
  }
  
  return updatedAffirmations;
};

export const generateBotResponse = (
  userMessage: string,
  currentStep: ManifestationStep,
  manifestationData: ManifestationData,
  options: ProcessOptions = {}
): { message: string; nextStep: ManifestationStep; updatedData: ManifestationData } => {
  // Create a deep copy of manifestation data to avoid mutation issues
  const updatedData = JSON.parse(JSON.stringify(manifestationData));
  let responseMessage = "";
  let nextStep = currentStep;
  
  const { isAffirmationSelection, selectedAffirmationIndex } = options;

  // Check if user is requesting to delete an affirmation
  if (userMessage.toLowerCase().includes("delete") && updatedData.savedAffirmations.length > 0) {
    // Parse the number from the delete command (e.g., "delete 2" -> 2)
    const match = userMessage.match(/delete\s+(\d+)/i);
    if (match && match[1]) {
      const affirmationNumber = parseInt(match[1]);
      
      if (!isNaN(affirmationNumber) && affirmationNumber > 0 && affirmationNumber <= updatedData.savedAffirmations.length) {
        // Remove the specified affirmation
        updatedData.savedAffirmations = updatedData.savedAffirmations.filter(
          (_, index) => index !== affirmationNumber - 1
        );
        
        responseMessage = `Deleted affirmation #${affirmationNumber}.`;
        
        // If there are still affirmations, show the updated list
        if (updatedData.savedAffirmations.length > 0) {
          responseMessage += "\n\nRemaining affirmations:\n\n" + 
            formatAffirmationOptions(updatedData.savedAffirmations);
        } else {
          responseMessage += "\n\nNo saved affirmations left. Create a new one?";
        }
        
        return {
          message: responseMessage,
          nextStep: currentStep, // Stay on the same step
          updatedData,
        };
      }
    }
  }

  switch (currentStep) {
    case "welcome":
      // If user typed a number and has saved affirmations, treat as affirmation selection
      const affirmationNum = parseInt(userMessage.trim());
      if (!isNaN(affirmationNum) && affirmationNum > 0 && affirmationNum <= updatedData.savedAffirmations.length) {
        const selectedAffirmation = updatedData.savedAffirmations[affirmationNum - 1];
        updatedData.presentTenseGoal = selectedAffirmation.text;
        responseMessage = `You selected: "${selectedAffirmation.text}"\n\nHow many manifestation cycles would you like to complete? (Choose a number between 1-12)\n\nℹ️ *A cycle is one round of writing, feeling, and visualizing your affirmation.*`;
        nextStep = "goal-setting";
      } else if (userMessage.toLowerCase().includes("new")) {
        responseMessage = getRandomMessage(stepPrompts["affirmation-choice"].message);
        if (stepPrompts["affirmation-choice"].followUp) {
          responseMessage += "\n\n" + getRandomMessage(stepPrompts["affirmation-choice"].followUp);
        }
        nextStep = "affirmation-choice";
      } else if (userMessage.toLowerCase().includes("exist")) {
        if (updatedData.savedAffirmations.length > 0) {
          responseMessage = "Which affirmation would you like to use? Type 'delete X' to remove #X.\n\n" + 
            formatAffirmationOptions(updatedData.savedAffirmations);
          nextStep = "goal-setting";
        } else {
          responseMessage = "No saved affirmations yet. Let's create a new one.\n\n" + 
            getRandomMessage(stepPrompts["affirmation-choice"].message);
          if (stepPrompts["affirmation-choice"].followUp) {
            responseMessage += "\n\n" + getRandomMessage(stepPrompts["affirmation-choice"].followUp);
          }
          nextStep = "affirmation-choice";
        }
      } else if (updatedData.savedAffirmations.length === 0) {
        // No saved affirmations - any response starts the new affirmation flow
        responseMessage = getRandomMessage(stepPrompts["affirmation-choice"].message);
        if (stepPrompts["affirmation-choice"].followUp) {
          responseMessage += "\n\n" + getRandomMessage(stepPrompts["affirmation-choice"].followUp);
        }
        nextStep = "affirmation-choice";
      } else {
        const invalidResponses = [
          "Please select an affirmation by number, or type 'new' to create one.",
          "Type a number to pick an affirmation, or 'new' to create a fresh one.",
          "Choose an affirmation number from the list, or type 'new'."
        ];
        responseMessage = getRandomMessage(invalidResponses);
        nextStep = "welcome";
      }
      break;

    case "affirmation-choice":
      // User has shared their goal
      updatedData.goal = userMessage;
      
      // Acknowledge the goal, then ask for present tense version
      // Use a placeholder for the AI-generated example that will be replaced async
      responseMessage = getRandomMessage(stepPrompts["goal-confirmation"].message)
        .replace("{goal}", userMessage);
        
      if (stepPrompts["goal-confirmation"].followUp) {
        responseMessage += "\n\n" + getRandomMessage(stepPrompts["goal-confirmation"].followUp)
          .replace("{goal}", userMessage)
          .replace("{presentTenseExample}", "{AI_REPHRASE_PENDING}");
      }
      
      nextStep = "goal-confirmation";
      break;
      
    case "goal-confirmation":
      // User has provided the present tense version of their goal
      updatedData.presentTenseGoal = userMessage;
      
      // Now ask for the number of cycles
      responseMessage = getRandomMessage(stepPrompts["goal-setting"].message);
      if (stepPrompts["goal-setting"].followUp) {
        responseMessage += "\n\n" + getRandomMessage(stepPrompts["goal-setting"].followUp)
          .replace("{presentTenseGoal}", userMessage);
      }
      
      nextStep = "goal-setting";
      break;

    case "goal-setting":
      // First scenario: User has already selected an affirmation by number
      if (isAffirmationSelection && selectedAffirmationIndex !== null) {
        console.log("Processing affirmation selection:", userMessage);
        
        // They already selected a saved affirmation (handled in useManifestation)
        updatedData.presentTenseGoal = userMessage; // This is the actual affirmation text now
        
        // Ask for cycle count with clear prompt
        responseMessage = `You selected: "${userMessage}"\n\n` + 
          "How many manifestation cycles would you like to complete? (Choose a number between 1-12)\n\nℹ️ *A cycle is one round of writing, feeling, and visualizing your affirmation.*";
          
        nextStep = "goal-setting"; // Stay in the same step to get cycle count
      }
      // Second scenario: User entered a number for cycle count after selecting an affirmation
      else if (/^(\d+)$/.test(userMessage) && updatedData.presentTenseGoal) {
        console.log("Processing cycle count after affirmation selection");
        
        // User entered a number for cycles
        const totalCycles = parseInt(userMessage);
updatedData.totalCycles = isNaN(totalCycles) ? 3 : Math.max(1, Math.min(12, totalCycles));
        updatedData.currentCycle = 1;
        
        // Prepare cycle setup message with cycle count
        let cycleSetupMsg = getRandomMessage(stepPrompts["cycle-setup"].message)
          .replace("{totalCycles}", updatedData.totalCycles.toString());
        
        responseMessage = cycleSetupMsg;
        
        // Add the follow-up with the affirmation
        if (stepPrompts["cycle-setup"].followUp) {
          const followUpMsg = getRandomMessage(stepPrompts["cycle-setup"].followUp)
            .replace("{currentCycle}", "1")
            .replace(/{presentTenseGoal}/g, updatedData.presentTenseGoal);
          
          responseMessage += "\n\n" + followUpMsg;
        }
        
        nextStep = "cycle-setup";
      } 
      // Third scenario: User provided a present tense statement directly
      else if (!isAffirmationSelection && !/^(\d+)$/.test(userMessage)) {
        // They provided a present tense statement directly
        updatedData.presentTenseGoal = userMessage;
        console.log("Set present tense goal:", updatedData.presentTenseGoal);
        
        // Affirmation will only be saved if user opts in at the end of the flow
        
        // Now ask for cycles
        responseMessage = "How many manifestation cycles would you like to complete? (Choose a number between 1-12)\n\nℹ️ *A cycle is one round of writing, feeling, and visualizing your affirmation.*";
        nextStep = "goal-setting";
      }
      // Fourth scenario: User entered a number but we can't determine if it's a cycle count or affirmation selection
      else if (/^(\d+)$/.test(userMessage)) {
        // If there are no saved affirmations, it must be a cycle count
        if (updatedData.savedAffirmations.length === 0) {
          const totalCycles = parseInt(userMessage);
          updatedData.totalCycles = isNaN(totalCycles) ? 3 : Math.max(1, Math.min(12, totalCycles));
          updatedData.currentCycle = 1;
          
          let cycleSetupMsg = getRandomMessage(stepPrompts["cycle-setup"].message)
            .replace("{totalCycles}", updatedData.totalCycles.toString());
          
          responseMessage = cycleSetupMsg;
          if (stepPrompts["cycle-setup"].followUp) {
            responseMessage += "\n\n" + getRandomMessage(stepPrompts["cycle-setup"].followUp)
              .replace("{currentCycle}", "1")
              .replace(/{presentTenseGoal}/g, updatedData.presentTenseGoal || "");
          }
          
          nextStep = "cycle-setup";
        } else {
          // If we have saved affirmations but user didn't select one first, ask for clarification
          responseMessage = "Please first select an affirmation from the list by number, or enter a new one.";
          nextStep = "goal-setting";
        }
      }
      break;

    case "cycle-setup":
      // Handle the cycle setup step
      if (updatedData.currentCycle === 0) {
        // First time in cycle-setup, parse the cycles
        const totalCycles = parseInt(userMessage.match(/\d+/)?.[0] || "3");
        updatedData.totalCycles = isNaN(totalCycles) ? 3 : Math.max(1, Math.min(12, totalCycles));
        updatedData.currentCycle = 1;
        
        // Make sure presentTenseGoal is set - Ensure it's always populated
        if (!updatedData.presentTenseGoal && updatedData.goal) {
          updatedData.presentTenseGoal = updatedData.goal;
          console.log("Using goal as presentTenseGoal:", updatedData.presentTenseGoal);
        }
        
        // Prepare cycle setup message with explicit placeholder replacement
        let cycleSetupMsg = getRandomMessage(stepPrompts["cycle-setup"].message)
          .replace("{totalCycles}", updatedData.totalCycles.toString());
        
        responseMessage = cycleSetupMsg;
        if (stepPrompts["cycle-setup"].followUp) {
          let followUpMsg = getRandomMessage(stepPrompts["cycle-setup"].followUp)
            .replace("{currentCycle}", "1");
          
          // Explicitly include the affirmation - Make sure we have the affirmation text
          if (updatedData.presentTenseGoal) {
            followUpMsg = followUpMsg.replace(/{presentTenseGoal}/g, updatedData.presentTenseGoal);
            console.log("Replaced presentTenseGoal in cycle-setup:", updatedData.presentTenseGoal);
          } else {
            followUpMsg = followUpMsg.replace(/{presentTenseGoal}/g, updatedData.goal || "NO GOAL FOUND");
            console.log("No presentTenseGoal found, using goal:", updatedData.goal);
          }
          
          responseMessage += `\n\n**Cycle 1 of ${updatedData.totalCycles}**\n\n` + followUpMsg;
        }
      } else {
        // Continuing with cycles - user has written their affirmation for this cycle
        responseMessage = getRandomMessage(stepPrompts["present-tense"].message);
        if (stepPrompts["present-tense"].followUp) {
          let followUpMsg = getRandomMessage(stepPrompts["present-tense"].followUp);
          
          // Explicitly include the affirmation
          if (updatedData.presentTenseGoal) {
            followUpMsg = followUpMsg.replace(/{presentTenseGoal}/g, updatedData.presentTenseGoal);
          } else if (updatedData.goal) {
            followUpMsg = followUpMsg.replace(/{presentTenseGoal}/g, updatedData.goal);
          } else {
            followUpMsg = followUpMsg.replace(/{presentTenseGoal}/g, "your goal");
          }
          
          responseMessage += "\n\n" + followUpMsg;
        }
        nextStep = "present-tense";
      }
      break;

    case "present-tense":
      // User has described their core feelings - now ask for deeper visualization
      updatedData.feelings = userMessage;

      // Deterministic rotation of visualization angles so cycles don't repeat
      const used = updatedData.usedVisualizationAngles || [];
      const nextAngle = VISUALIZATION_ANGLES.find(a => !used.includes(a))
        || VISUALIZATION_ANGLES[used.length % VISUALIZATION_ANGLES.length];
      updatedData.usedVisualizationAngles = [...used, nextAngle];

      responseMessage = getRandomMessage(stepPrompts["feeling-state"].message);
      responseMessage += "\n\n" + nextAngle;
      nextStep = "feeling-state";
      break;

    case "feeling-state":
      // User has described their visualization - now ask about gap
      updatedData.currentReality = userMessage;
      
      responseMessage = getRandomMessage(stepPrompts["respond"].message);
      if (stepPrompts["respond"].followUp) {
        let followUpMsg = getRandomMessage(stepPrompts["respond"].followUp);
        if (updatedData.presentTenseGoal) {
          followUpMsg = followUpMsg.replace(/{presentTenseGoal}/g, updatedData.presentTenseGoal);
        }
        responseMessage += "\n\n" + followUpMsg;
      }
      nextStep = "respond";
      break;

    case "respond":
      // Validate: must contain a number 1-10
      const ratingMatch = userMessage.match(/\b(10|[1-9])\b/);
      if (!ratingMatch) {
        responseMessage = "I didn't catch a number - on a scale of **1 to 10**, how real does this manifestation feel right now?";
        nextStep = "respond";
        break;
      }

      // User has given their 1-10 rating - auto-advance (no separate "Ready?" step).
      updatedData.alignment = userMessage;
      const rating = parseInt(ratingMatch[1]);

      const lowAcks = [
        "Honest read - that's the starting point.",
        "Noted. We'll build from here.",
        "That's a real number to work with.",
        "Thanks for the honesty - awareness first.",
      ];
      const midAcks = [
        "Solid middle ground.",
        "You're finding your footing with it.",
        "The belief is starting to take hold.",
        "Good - it's landing.",
      ];
      const highAcks = [
        "You're clearly locked in.",
        "Strong alignment.",
        "That belief is doing real work.",
        "You're moving with it now.",
      ];
      const almostAcks = [
        "You're basically already there.",
        "This is the frequency - stay here.",
        "Fully in it. Beautiful.",
      ];

      let ratingAck = "";
      if (rating <= 3) {
        ratingAck = getRandomMessage(lowAcks);
      } else if (rating <= 6) {
        ratingAck = getRandomMessage(midAcks);
      } else if (rating <= 8) {
        ratingAck = getRandomMessage(highAcks);
      } else {
        ratingAck = getRandomMessage(almostAcks);
      }


      const isLastCycleR = updatedData.currentCycle === updatedData.totalCycles;

      if (isLastCycleR) {
        // Last cycle → jump straight to core-feeling using the emotion already
        // captured in this cycle's `feelings`. Skips the redundant "what's the
        // emotion?" ask in the old `summary` step (per user review).
        updatedData.coreFeeling = extractCoreFeeling(updatedData.feelings || userMessage);

        let coreFeelingsMsg = getRandomMessage(stepPrompts["core-feeling"].message)
          .replace("{coreFeeling}", updatedData.coreFeeling);
        if (stepPrompts["core-feeling"].followUp) {
          coreFeelingsMsg += "\n\n" + getRandomMessage(stepPrompts["core-feeling"].followUp)
            .replace("{coreFeeling}", updatedData.coreFeeling);
        }

        responseMessage = ratingAck + "\n\n---\n\n" + coreFeelingsMsg;
        nextStep = "core-feeling";
      } else {
        // Not last cycle → auto-advance straight into the next cycle's write-out
        // prompt, so the user doesn't have to type "ready".
        updatedData.currentCycle += 1;

        const nextCyclePrompts = [
          `**Cycle ${updatedData.currentCycle} of ${updatedData.totalCycles}**\n\nType out your affirmation once more:\n"${updatedData.presentTenseGoal}"\n\nThis time, feel it like it's already behind you - you're looking back with pride.`,
          `**Cycle ${updatedData.currentCycle} of ${updatedData.totalCycles}**\n\nType out your affirmation again:\n"${updatedData.presentTenseGoal}"\n\nAs you type it this time, imagine you're already on the other side, celebrating this reality.`,
          `**Cycle ${updatedData.currentCycle} of ${updatedData.totalCycles}**\n\nType out your affirmation once more:\n"${updatedData.presentTenseGoal}"\n\nType it as if you're telling the story of something that has already happened - because energetically, it has.`,
          `**Cycle ${updatedData.currentCycle} of ${updatedData.totalCycles}**\n\nType out your affirmation again:\n"${updatedData.presentTenseGoal}"\n\nThis time, embody the feeling of looking back at a fulfilled wish - something that's now part of your history.`,
          `**Cycle ${updatedData.currentCycle} of ${updatedData.totalCycles}**\n\nType out your affirmation:\n"${updatedData.presentTenseGoal}"\n\nAs you type it, imagine yourself in the future, remembering the moment this desire manifested completely.`
        ];

        responseMessage = ratingAck + "\n\n---\n\n" + getRandomMessage(nextCyclePrompts);
        nextStep = "cycle-setup";
      }
      break;

    case "reality-check":
      // Legacy path - should rarely be hit now that `respond` auto-advances.
      // Kept as a safety net: treat any input here as "continue" and route
      // to core-feeling or the next cycle.
      const isLastCycleRC = updatedData.currentCycle === updatedData.totalCycles;

      if (isLastCycleRC) {
        updatedData.coreFeeling = updatedData.coreFeeling || extractCoreFeeling(updatedData.feelings || userMessage);
        let coreMsg = getRandomMessage(stepPrompts["core-feeling"].message)
          .replace("{coreFeeling}", updatedData.coreFeeling);
        if (stepPrompts["core-feeling"].followUp) {
          coreMsg += "\n\n" + getRandomMessage(stepPrompts["core-feeling"].followUp)
            .replace("{coreFeeling}", updatedData.coreFeeling);
        }
        responseMessage = coreMsg;
        nextStep = "core-feeling";
      } else {
        updatedData.currentCycle += 1;
        responseMessage = `**Cycle ${updatedData.currentCycle} of ${updatedData.totalCycles}**\n\nType out your affirmation once more:\n"${updatedData.presentTenseGoal}"`;
        nextStep = "cycle-setup";
      }
      break;

    case "summary":
      // User has shared their positive emotions after all cycles
      // This now leads to the core-feeling step
      updatedData.feelings = userMessage;
      updatedData.coreFeeling = extractCoreFeeling(userMessage);
      
      // Get the core feeling step message
      let coreFeelingsMsg = getRandomMessage(stepPrompts["core-feeling"].message)
        .replace("{coreFeeling}", updatedData.coreFeeling);
      
      if (stepPrompts["core-feeling"].followUp) {
        coreFeelingsMsg += "\n\n" + getRandomMessage(stepPrompts["core-feeling"].followUp)
          .replace("{coreFeeling}", updatedData.coreFeeling);
      }
      
      responseMessage = coreFeelingsMsg;
      nextStep = "core-feeling";
      break;

    case "core-feeling":
      // User has shared where they already experience the core feeling.
      // Per user review: skip the redundant "would you like to begin another
      // manifestation?" intermediate ask and go straight to the save prompt.
      updatedData.currentReality = userMessage;

      const alreadySavedCF = updatedData.savedAffirmations.some(
        aff => aff.text.toLowerCase() === (updatedData.presentTenseGoal || "").toLowerCase()
      );

      const bridgeAck = getRandomMessage(stepPrompts["current-reality"].message)
        .replace("{coreFeeling}", updatedData.coreFeeling || "this feeling");

      if (updatedData.presentTenseGoal && !alreadySavedCF) {
        responseMessage = bridgeAck +
          "\n\nBefore we wrap up - would you like to save this affirmation for future sessions? (yes/no)";
        nextStep = "save-prompt";
      } else {
        responseMessage = bridgeAck + "\n\n" + generateFinalSummary(updatedData);
        nextStep = "completed";
      }
      break;

    case "current-reality":
      // Legacy safety net - same behavior as core-feeling now.
      const alreadySavedCR = updatedData.savedAffirmations.some(
        aff => aff.text.toLowerCase() === (updatedData.presentTenseGoal || "").toLowerCase()
      );

      if (updatedData.presentTenseGoal && !alreadySavedCR) {
        responseMessage =
          "Before we wrap up - would you like to save this affirmation for future sessions? (yes/no)";
        nextStep = "save-prompt";
      } else {
        responseMessage = generateFinalSummary(updatedData);
        nextStep = "completed";
      }
      break;

    case "save-prompt":
      const wantsSave = userMessage.toLowerCase().includes("yes") ||
                        userMessage.toLowerCase().includes("save") ||
                        userMessage.toLowerCase().includes("yeah") ||
                        userMessage.toLowerCase().includes("yep") ||
                        userMessage.toLowerCase().includes("sure");

      let saveConfirmation = "";
      if (wantsSave && updatedData.presentTenseGoal) {
        const newAffirmation: SavedAffirmation = {
          id: generateUniqueId(),
          text: updatedData.presentTenseGoal,
          createdAt: new Date(),
          completed: true,
          completedAt: new Date()
        };
        updatedData.savedAffirmations = [...updatedData.savedAffirmations, newAffirmation];
        saveConfirmation = `✅ Saved! Your affirmation **"${updatedData.presentTenseGoal}"** is ready for future sessions.`;
      } else {
        saveConfirmation = "No problem - your affirmation won't be saved.";
      }

      // Completion celebration now comes AFTER the save decision
      responseMessage = saveConfirmation + "\n\n" + generateFinalSummary(updatedData);
      nextStep = "completed";
      break;

    default:
      // Variations for fallback messages
      const fallbackMessages = [
        "Let's start over. New affirmation or use existing?",
        "Let's reset. Create new or use saved affirmation?",
        "Let's begin again. New or existing affirmation?"
      ];
      responseMessage = getRandomMessage(fallbackMessages);
      nextStep = "welcome";
      break;
  }

  // Double-check that all placeholders are replaced before returning
  if (responseMessage.includes("{presentTenseGoal}") && updatedData.presentTenseGoal) {
    responseMessage = responseMessage.replace(/{presentTenseGoal}/g, updatedData.presentTenseGoal);
  } else if (responseMessage.includes("{presentTenseGoal}") && updatedData.goal) {
    responseMessage = responseMessage.replace(/{presentTenseGoal}/g, updatedData.goal);
  }
  
  if (responseMessage.includes("{totalCycles}") && updatedData.totalCycles) {
    responseMessage = responseMessage.replace(/{totalCycles}/g, updatedData.totalCycles.toString());
  }
  
  if (responseMessage.includes("{currentCycle}") && updatedData.currentCycle) {
    responseMessage = responseMessage.replace(/{currentCycle}/g, updatedData.currentCycle.toString());
  }
  
  return {
    message: responseMessage,
    nextStep,
    updatedData,
  };
};

// Helper function to extract core feeling from user message
const extractCoreFeeling = (message: string): string => {
  // Simple extraction of key emotional words
  const emotions = ["relief", "gratitude", "joy", "excitement", "peace", "happiness", "abundance", "love", "freedom", "confidence", "security", "achievement", "satisfaction", "belonging", "purpose"];
  
  // Extract words and clean them
  const words = message.toLowerCase()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
    .split(/\s+/);
  
  // Find emotions in the message
  const foundEmotions = emotions.filter(emotion => words.includes(emotion));
  
  if (foundEmotions.length > 0) {
    return foundEmotions[0]; // Return the first found emotion
  }
  
  // If no specific emotion found, extract a potential feeling word
  const potentialFeelings = words.filter(word => 
    word.length > 3 && 
    !["this", "that", "then", "when", "what", "have", "would", "will", "feel", "feeling", "feels", "felt"].includes(word)
  );
  
  if (potentialFeelings.length > 0) {
    return potentialFeelings[0]; // Return the first potential feeling word
  }
  
  // Default if no emotions detected
  return "positive feeling";
};

// Helper function to extract feelings from user message
const extractFeelings = (message: string): string => {
  // Simple extraction of key emotional words
  const emotions = ["relief", "gratitude", "joy", "excitement", "peace", "happiness", "abundance", "love", "freedom"];
  const words = message.toLowerCase().split(/\s+/);
  
  const foundEmotions = emotions.filter(emotion => words.includes(emotion));
  
  if (foundEmotions.length > 0) {
    return foundEmotions.join(' and ');
  }
  
  // Default if no emotions detected
  return "these positive emotions";
};

// Generate the summary based on all collected data for each cycle
const generateSummary = (data: ManifestationData): string => {
  return `
Here's a summary of your manifestation journey for cycle ${data.currentCycle}:

1. **Your Goal:** ${data.goal}

2. **Present Tense Statement:** "${data.presentTenseGoal}"

3. **How It Feels:** When you imagine this reality, you feel ${data.feelings}

4. **Personal Insight:** ${data.alignment}

${data.currentCycle === data.totalCycles ?
    "You're about to complete all your manifestation cycles! Just one final step remains after this." :
    `We're making great progress! You've completed cycle ${data.currentCycle} of ${data.totalCycles}. Let's continue the journey with your next cycle.`}
  `;
};

// Generate the final summary that includes the current reality connection
const generateFinalSummary = (data: ManifestationData): string => {
  const cycles = data.totalCycles || 0;
  const affirmation = data.presentTenseGoal || "your affirmation";
  const bridge = formatBridge(data.currentReality);

  return `
🎉 Congratulations on completing your full manifestation practice! 🎉

You have officially DM'd the Universe! The energy of this process is now in the field working its magic.

Here's your complete manifestation journey:

1. **Your Affirmation:** **"${affirmation}"** - you moved through ${cycles} ${cycles === 1 ? "cycle" : "cycles"} of writing, feeling, and visualizing it as already true.

2. **Present Tense Awareness:** You practiced speaking and thinking as if your desire is already real.

3. **Feeling Connection:** You tapped into the emotions that align you with your manifestation.

4. **Reality Bridge:** You've named where these feelings already show up in your life: ${bridge}

This connection is the secret to successful manifestation - by recognizing and appreciating where your desire already exists (even in small ways), you naturally expand those experiences.

✨ Your manifestation process is now complete. ✨ The energy you've put into this practice is at work, bringing your desires into reality.
  `.trim();
};

// Format the "current reality" answer so short fragments read naturally in the summary.
const formatBridge = (raw?: string): string => {
  const text = (raw || "").trim().replace(/[.!?]+$/, "");
  if (!text) return "the moments you named earlier.";
  const wordCount = text.split(/\s+/).length;
  if (wordCount <= 4) {
    const lower = text.charAt(0).toLowerCase() + text.slice(1);
    return `**"${lower}"** - even that small anchor is enough to build from.`;
  }
  return `**"${text}."**`;
};

export const generateUniqueId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};
